import classNames from 'classnames';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import React from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { LOCALES } from 'src/constants/main';
import { TTranslate } from 'src/types/main';
import LanguageButtonSwitcher from 'src/view/components/Controls/LanguageButtonSwitcher/LanguageButtonSwitcher';
import { localeStore } from '~/mobx/localesStore';
import { isMultiLanguageValid } from '~/helpers';
// eslint-disable-next-line max-len
import MultiLanguageInputErrorText from '~/view/components/Controls/MultiLanguageInput/components/MultiLanguageInputErrorText/MultiLanguageInputErrorText';
import { IMultiLanguageInputValidation } from '~/view/components/Controls/MultiLanguageInput/types';
import './react-draft-wysiwyg.scss';
import './style.scss';

interface IProps {
  enValue?: string;
  arValue?: string;
  customToolbar?: object;
  onChange: (value: TTranslate) => void;
  validation?: IMultiLanguageInputValidation;
}

interface IState {
  editorState: EditorState;
  language: LOCALES;
  enValue: string;
  arValue: string;
}

export default class MultiLanguageTextEditor extends React.Component<IProps, IState> {
  state = {
    editorState: EditorState.createEmpty(),
    language: localeStore.language,
    enValue: this.props.enValue || '',
    arValue: this.props.arValue || '',
  };

  constructor(props: IProps) {
    super(props);
    const contentBlock = htmlToDraft(this.props.enValue || '');
    if (this.props.enValue && contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      const editorState = EditorState.createWithContent(contentState);
      this.state = {
        ...this.state,
        editorState,
      };
    } else {
      this.state = {
        ...this.state,
        editorState: EditorState.createEmpty(),
      };
    }
  }

  onChangeLanguage = (locale?: LOCALES) => {
    this.setState({ language: locale || LOCALES.EN });

    const value = locale === LOCALES.AR ? this.props.arValue : this.props.enValue;
    const contentBlock = htmlToDraft(value || '');

    if (value && contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      const editorState = EditorState.createWithContent(contentState);
      this.onEditorStateChange(editorState);
    }
  };

  onEditorStateChange = (editorState: EditorState) => {
    this.setState({ editorState });
  };

  onChange = () => {
    const value = draftToHtml(convertToRaw(this.state.editorState.getCurrentContent()));

    if (this.state.language === LOCALES.EN) {
      this.setState({ enValue: value });
    } else {
      this.setState({ arValue: value });
    }

    this.props.onChange({ en: this.state.enValue, ar: this.state.arValue });
  };

  get isValid(): boolean {
    return isMultiLanguageValid(this.props.validation);
  }

  get toolbar() {
    if (this.props.customToolbar) return this.props.customToolbar;

    return {
      options: ['blockType', 'inline', 'textAlign', 'list'],
      inline: {
        options: ['bold', 'italic', 'underline'],
        className: 'text-editor__inline',
      },
      list: {
        options: ['unordered', 'ordered'],
        className: 'text-editor__list',
      },
      textAlign: {
        options: ['left', 'center', 'right'],
        className: 'text-editor__text-align',
      },
      blockType: {
        inDropdown: true,
        options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'],
        className: 'text-editor__block-type',
        dropdownClassName: 'text-editor__block-type__dropdown',
      },
    };
  }

  render() {
    const { editorState } = this.state;

    return (
      <div className="text-editor__wrapper">
        <Editor
          toolbar={this.toolbar}
          editorState={editorState}
          toolbarClassName="text-editor__header"
          editorClassName={classNames('text-editor__editor', { 'input-error': !this.isValid })}
          onEditorStateChange={this.onEditorStateChange}
          onChange={this.onChange}
        />
        <MultiLanguageInputErrorText isValid={this.isValid} currentLanguage={this.state.language} validation={this.props.validation} />
        <LanguageButtonSwitcher
          language={this.state.language}
          setLanguage={this.onChangeLanguage}
          validation={this.props.validation}
          className="text-editor__lang-switcher"
        />
      </div>
    );
  }
}
